import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { ImFinancialViz } from "../../components/ImFinancialViz";
import ImStrategyForm from "../../components/ImStrategyForm";
import ProformaEditor from "../../components/Proforma";
import { ArrowLeft, RefreshCw, BookOpen, Send, CheckCircle2, Pencil, ShieldCheck, Sparkles, LineChart, Eye } from "lucide-react";
import { toast } from "sonner";
import TeaserMarkdown from "../../components/TeaserMarkdown";
import { ImPdfButton } from "../../components/ImPdfButton";
import { CLOSED_YEARS } from "../../lib/stages";
import { t } from "../../lib/i18n";

const IM_SECTIONS = [
  { key: "executive_summary", label: t("Resumen Ejecutivo"), hint: t("Vende la oportunidad: qué hace la empresa, sus cifras clave y por qué es una inversión atractiva.") },
  { key: "market_analysis", label: t("Análisis del Mercado"), hint: t("Aporta detalles estratégicos para atraer mejores ofertas: tamaño del mercado, tendencias y tu posición diferencial.") },
  { key: "operations", label: t("Operaciones"), hint: t("Explica tu visión de crecimiento para validar el precio: equipo, procesos y palancas concretas para el comprador.") },
  { key: "financial_hygiene", label: t("Fortaleza Financiera"), hint: t("Destaca márgenes estables, recurrencia y generación de caja; convierte cada cifra en un argumento.") },
];

const FIN_FIELDS = [
  { key: "revenue_y1", label: `Ingresos ${CLOSED_YEARS[0]} (€)` },
  { key: "revenue_y2", label: `Ingresos ${CLOSED_YEARS[1]} (€)` },
  { key: "revenue_y3", label: `Ingresos ${CLOSED_YEARS[2]} · último ejercicio (€) *` },
  { key: "gross_y1", label: `Margen bruto ${CLOSED_YEARS[0]} (€)` },
  { key: "gross_y2", label: `Margen bruto ${CLOSED_YEARS[1]} (€)` },
  { key: "gross_y3", label: `Margen bruto ${CLOSED_YEARS[2]} · último ejercicio (€) *` },
  { key: "ebitda_y1", label: `EBITDA ${CLOSED_YEARS[0]} (€)` },
  { key: "ebitda_y2", label: `EBITDA ${CLOSED_YEARS[1]} (€)` },
  { key: "ebitda_y3", label: `EBITDA ${CLOSED_YEARS[2]} · último ejercicio (€) *` },
  { key: "net_debt", label: t("Deuda financiera neta (€)") },
  { key: "recurring_revenue_pct", label: t("Ingresos recurrentes (%)") },
  { key: "top3_client_pct", label: t("Concentración top-3 clientes (%)") },
  { key: "capex_avg", label: t("CAPEX anual medio (€)") },
  { key: "growth_pct", label: t("Crecimiento anual esperado 3 años (%) *") },
];

const toNum = (v) => (v === "" || v === undefined || v === null || Number.isNaN(Number(v)) ? null : Number(v));

// Mapea los datos de la etapa 2 (Financieros) a los campos del IM.
const prefillFromStage2 = (company) => {
  const s2 = company.data?.["2"] || {};
  return {
    revenue_y1: toNum(s2.revenue_y1_eur),
    revenue_y2: toNum(s2.revenue_y2_eur),
    revenue_y3: toNum(s2.revenue_eur),
    gross_y3: toNum(s2.gross_eur),
    ebitda_y1: toNum(s2.ebitda_y1_eur),
    ebitda_y2: toNum(s2.ebitda_y2_eur),
    ebitda_y3: toNum(s2.ebitda_eur),
    growth_pct: toNum(s2.growth_pct),
    net_debt: toNum(s2.net_debt_eur),
  };
};

export default function ImReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [finForm, setFinForm] = useState({});
  const [showFinForm, setShowFinForm] = useState(false);
  const [savingFin, setSavingFin] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    api.get(`/companies/${id}`).then(async (r) => {
      const co = r.data;
      if (!co.im_financials) {
        const pre = prefillFromStage2(co);
        const complete = pre.revenue_y3 !== null && pre.ebitda_y3 !== null && pre.growth_pct !== null && pre.gross_y3 !== null;
        if (complete) {
          try {
            const saved = await api.put(`/companies/${id}/im/financials`, pre);
            setCompany(saved.data.company);
            setFinForm(saved.data.company.im_financials || {});
            setShowFinForm(false);
            setPrefilled(true);
            return;
          } catch { /* cae al formulario pre-rellenado */ }
        }
        setCompany(co);
        setFinForm(Object.fromEntries(Object.entries(pre).filter(([, v]) => v !== null)));
        setShowFinForm(true);
        setPrefilled(Object.values(pre).some((v) => v !== null));
        return;
      }
      setCompany(co);
      setFinForm(co.im_financials || {});
      setShowFinForm(false);
    }).catch(() => toast.error(t("Empresa no disponible")));
  }, [id]);

  const saveFinancials = async () => {
    const required = ["revenue_y3", "gross_y3", "ebitda_y3", "growth_pct"];
    for (const k of required) {
      if (finForm[k] === undefined || finForm[k] === null || finForm[k] === "") {
        toast.error(t("Completa los campos obligatorios (*): incluye el margen bruto del último ejercicio para que el gráfico muestre Ingresos, Margen bruto y EBITDA"));
        return;
      }
    }
    setSavingFin(true);
    try {
      const payload = {};
      FIN_FIELDS.forEach(({ key }) => {
        payload[key] = finForm[key] === "" || finForm[key] === undefined ? null : Number(finForm[key]);
      });
      const r = await api.put(`/companies/${id}/im/financials`, payload);
      setCompany(r.data.company);
      setFinForm(r.data.company.im_financials || {});
      setShowFinForm(false);
      toast.success(t("Datos financieros guardados · proyección calculada"));
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error guardando los datos");
    } finally {
      setSavingFin(false);
    }
  };

  const generate = async () => {
    if (!company.im_financials) {
      toast.error(t("Completa primero los datos para proyección financiera"));
      setShowFinForm(true);
      return;
    }
    setGenerating(true);
    const prevGeneratedAt = company.im_generated_at;
    try {
      const r = await api.post(`/companies/${id}/im/generate`);
      setCompany(r.data.company);
      toast.success(t("Information Memorandum generado con IA"));
    } catch (e) {
      const status = e.response?.status;
      const d = e.response?.data?.detail;
      // El proxy corta a los 60s pero el backend sigue generando: esperamos el resultado.
      if (!status || status >= 502) {
        toast.message(t("La IA sigue redactando… esperando el resultado."));
        for (let i = 0; i < 18; i++) {
          await new Promise((res) => setTimeout(res, 5000));
          try {
            const c = await api.get(`/companies/${id}`);
            if (c.data.im_generated_at && c.data.im_generated_at !== prevGeneratedAt) {
              setCompany(c.data);
              toast.success(t("Information Memorandum generado con IA"));
              setGenerating(false);
              return;
            }
          } catch { /* reintenta */ }
        }
      }
      toast.error(typeof d === "string" ? d : "Error generando el IM. Inténtalo de nuevo.");
    } finally {
      setGenerating(false);
    }
  };

  const saveSection = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/companies/${id}/im`, { sections: { [editingKey]: draft } });
      setCompany(r.data.company);
      setEditingKey(null);
      toast.success(t("Sección actualizada"));
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async () => {
    setPublishing(true);
    const action = company.im_published ? "unpublish" : "publish";
    try {
      const r = await api.post(`/companies/${id}/im/${action}`);
      setCompany(r.data.company);
      toast.success(action === "publish" ? "IM publicado · visible para NDAs aprobados" : "IM retirado");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error");
    } finally {
      setPublishing(false);
    }
  };

  if (!company) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <span className="font-mono-label text-muted-foreground">{t("Cargando…")}</span>
        </div>
      </Layout>
    );
  }

  const sections = company.im_sections;
  const stage1 = company.data?.["1"] || {};
  const fin = company.im_financials || {};
  const kpis = {
    revenue: fin.revenue_y3,
    ebitda: fin.ebitda_y3,
    ebitda_margin_pct: fin.revenue_y3 ? Math.round((fin.ebitda_y3 / fin.revenue_y3) * 1000) / 10 : null,
    employees: stage1.employees,
    net_debt: fin.net_debt,
    recurring_revenue_pct: fin.recurring_revenue_pct,
    top3_client_pct: fin.top3_client_pct,
    capex_avg: fin.capex_avg,
    growth_pct: fin.growth_pct,
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
        <Link
          to={`/seller/company/${id}`}
          className="font-mono-label text-muted-foreground inline-flex items-center gap-2 mb-8 hover:text-foreground"
          data-testid="im-back"
        >
          <ArrowLeft className="w-3 h-3" />{" "}{t("Volver al detalle de la empresa")}
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-3">
          <div>
            <span className="font-mono-label text-gold">{t("Information Memorandum ·")}{" "}{company.name}</span>
            <h1 className="font-serif-display text-4xl md:text-5xl mt-2" data-testid="im-editor-title">{t("IM editable")}</h1>
          </div>
          {company.im_published ? (
            <span className="hairline px-3 py-1.5 bg-forest/10 text-forest border-forest/30 font-mono-label" data-testid="im-status">
              {t("Publicado · los cambios que guardes aquí se reflejan en el IM publicado")}
            </span>
          ) : (
            <span className="hairline px-3 py-1.5 bg-bone font-mono-label text-muted-foreground" data-testid="im-status">
              {t("Borrador · aún no visible")}
            </span>
          )}
        </div>

        <p className="text-muted-foreground max-w-2xl mb-10">
          {t("Este documento solo lo verán searchers e inversores cuyo NDA firmado hayas aprobado. A diferencia del teaser, el IM sí revela el nombre real de tu empresa e incluye un P&L proforma con proyecciones financieras justificadas por ti.")}
        </p>

        <ImStrategyForm company={company} onCompanyUpdate={setCompany} />

        <div className="hairline bg-card mb-8" data-testid="im-financials-card">
          <div className="px-6 py-4 hairline-b bg-bone flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <LineChart className="w-4 h-4 text-gold" />
              <div>
                <span className="font-mono-label text-gold">{t("Datos para proyección financiera")}</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("Información privada, no pública en el marketplace. Solo se usa para el IM y su proyección a 3 años.")}
                </p>
              </div>
            </div>
            {company.im_financials && (
              <button
                type="button"
                onClick={() => setShowFinForm((v) => !v)}
                className="font-mono-label text-gold inline-flex items-center gap-1 hover:opacity-70"
                data-testid="im-fin-toggle"
              >
                <Pencil className="w-3 h-3" /> {showFinForm ? t("Cerrar") : t("Editar datos")}
              </button>
            )}
          </div>

          {company.im_financials && !showFinForm && company.im_financials.gross_y3 == null && !toNum(company.data?.["2"]?.gross_eur) && (
            <div className="px-6 py-3 hairline-b bg-[#E0712C]/10 flex flex-wrap items-center justify-between gap-2 text-sm text-[#C25F1E]" data-testid="im-fin-missing-gross">
              <span>{t("Falta el")}{" "}<strong>{t("margen bruto del último ejercicio")}</strong>{t(": el gráfico del IM solo mostrará Ingresos y EBITDA hasta que lo añadas.")}</span>
              <button type="button" onClick={() => setShowFinForm(true)} className="font-mono-label underline underline-offset-4" data-testid="im-fin-missing-gross-btn">
                {t("Añadir margen bruto")}
              </button>
            </div>
          )}

          {prefilled && (
            <div className="px-6 py-3 hairline-b bg-forest/5 flex items-center gap-2 text-sm text-forest" data-testid="im-fin-prefilled">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              {t("Ingresos y EBITDA de los 3 ejercicios cerrados, crecimiento y deuda neta se han tomado de la etapa 2 (Financieros). Puedes ampliarlos o corregirlos con \"Editar datos\".")}
            </div>
          )}

          {showFinForm && (
            <div className="p-6" data-testid="im-fin-form">
              <div className="grid md:grid-cols-3 gap-5">
                {FIN_FIELDS.map(({ key, label }) => (
                  <div key={key}>
                    <Label className="font-mono-label text-muted-foreground" style={{ fontSize: "0.6rem" }}>{label}</Label>
                    <Input
                      type="number"
                      value={finForm[key] ?? ""}
                      onChange={(e) => setFinForm((p) => ({ ...p, [key]: e.target.value }))}
                      className="rounded-none mt-1.5"
                      data-testid={`im-fin-${key}`}
                    />
                  </div>
                ))}
              </div>
              <Button
                onClick={saveFinancials}
                disabled={savingFin}
                className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-10 px-6 mt-6"
                data-testid="im-fin-save"
              >
                {savingFin ? t("Guardando…") : t("Guardar y calcular proyección")}
              </Button>
            </div>
          )}

          {company.im_projection && !showFinForm && (
            <div className="p-4">
              <ImFinancialViz kpis={kpis} projection={company.im_projection} />
            </div>
          )}
        </div>

        <ProformaEditor company={company} onCompanyUpdate={setCompany} />

        {!sections ? (
          <div className="hairline bg-card p-12 text-center" data-testid="im-empty-state">
            <BookOpen className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-serif-display text-2xl mb-2">{t("Genera tu Information Memorandum")}</h3>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              {t("Nuestra IA redactará las 4 secciones (Resumen Ejecutivo, Análisis del Mercado, Operaciones y Fortaleza Financiera) con un enfoque comercial M&A a partir de tus etapas anteriores, tu visión estratégica y la proyección financiera. Cuanto más hayas contado, más persuasivo será el resultado.")}
            </p>
            {!company.im_financials && (
              <p className="font-mono-label text-destructive mb-4" data-testid="im-fin-required">
                {t("Completa primero los datos para proyección financiera (arriba)")}
              </p>
            )}
            <Button
              onClick={generate}
              disabled={generating || !company.im_financials}
              className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-12 px-8 disabled:opacity-50"
              data-testid="im-generate-btn"
            >
              <Sparkles className={`w-4 h-4 mr-2 ${generating ? "animate-pulse" : ""}`} />
              {generating ? t("Generando con IA… (puede tardar ~1 min)") : t("Generar IM con IA")}
            </Button>
          </div>
        ) : (
          <div className="hairline bg-card">
            <div className="px-7 py-4 hairline-b bg-[#1E3A5F] text-bone flex flex-wrap items-center justify-between gap-3" data-testid="im-editor-mode-banner">
              <span className="flex items-center gap-3">
                <Pencil className="w-4 h-4 text-gold" />
                <span className="font-mono-label text-gold">{t("Modo edición · esta es tu versión editable, no la vista del inversor")}</span>
              </span>
              <button
                type="button"
                onClick={() => navigate(`/im/${id}?preview=1`)}
                className="font-mono-label text-bone underline underline-offset-4 hover:text-gold inline-flex items-center gap-1.5"
                data-testid="im-editor-preview-link"
              >
                <Eye className="w-3.5 h-3.5" />{" "}{t("Ver el IM publicado tal y como lo reciben searchers e inversores")}
              </button>
            </div>

            <Accordion type="multiple" defaultValue={IM_SECTIONS.map((s) => s.key)} className="px-2">
              {IM_SECTIONS.map((s) => (
                <AccordionItem key={s.key} value={s.key} className="border-b border-border/60">
                  <AccordionTrigger className="px-5 hover:no-underline" data-testid={`im-section-trigger-${s.key}`}>
                    <span className="font-serif-display text-2xl">{s.label}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-8">
                    {editingKey === s.key ? (
                      <div className="space-y-3">
                        <p className="text-xs text-[#C25F1E] flex items-start gap-1" data-testid={`im-editor-hint-${s.key}`}>
                          <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" /> {s.hint}
                        </p>
                        <Textarea
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          rows={12}
                          placeholder={s.hint}
                          className="rounded-none font-mono text-sm w-full"
                          data-testid={`im-editor-${s.key}`}
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={saveSection}
                            disabled={saving}
                            className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-9 px-4"
                            data-testid={`im-save-${s.key}`}
                          >
                            {saving ? t("Guardando…") : t("Guardar sección")}
                          </Button>
                          <Button onClick={() => setEditingKey(null)} variant="outline" className="rounded-none h-9 px-4">
                            {t("Cancelar")}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <article className="teaser-prose" data-testid={`im-content-${s.key}`}>
                          <TeaserMarkdown text={sections[s.key] || ""} stripLeadingHeading />
                        </article>
                        <button
                          type="button"
                          onClick={() => { setEditingKey(s.key); setDraft(sections[s.key] || ""); }}
                          className="font-mono-label text-gold mt-4 inline-flex items-center gap-1 hover:opacity-70"
                          data-testid={`im-edit-${s.key}`}
                        >
                          <Pencil className="w-3 h-3" />{" "}{t("Editar sección")}
                        </button>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="px-7 py-5 hairline-t bg-bone flex flex-wrap items-center justify-between gap-3">
              <Button
                onClick={generate}
                variant="outline"
                disabled={generating}
                className="rounded-none h-10 px-5"
                data-testid="im-regenerate-btn"
              >
                <RefreshCw className={`w-3 h-3 mr-2 ${generating ? "animate-spin" : ""}`} />
                {generating ? t("Regenerando…") : t("Regenerar con IA")}
              </Button>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => navigate(`/im/${id}?preview=1`)}
                  variant="outline"
                  className="rounded-none h-10 px-5 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F]/5"
                  data-testid="im-preview-btn"
                >
                  <Eye className="w-3 h-3 mr-2" />{" "}{t("Previsualizar IM publicado")}
                </Button>
                <ImPdfButton companyId={id} companyName={company.name} />
                {company.im_published ? (
                  <Button
                    onClick={togglePublish}
                    disabled={publishing}
                    variant="outline"
                    className="rounded-none h-10 px-5 border-destructive text-destructive hover:bg-destructive/5"
                    data-testid="im-unpublish-btn"
                  >
                    {t("Retirar el IM")}
                  </Button>
                ) : (
                  <Button
                    onClick={togglePublish}
                    disabled={publishing}
                    className="rounded-none text-bone h-10 px-6"
                    style={{ backgroundColor: "#2E6F80" }}
                    data-testid="im-publish-btn"
                  >
                    <Send className="w-3 h-3 mr-2" /> {publishing ? t("Publicando…") : t("Publicar IM")}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {company.im_published && (
          <div className="mt-6 hairline p-5 bg-forest/5 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-forest mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <div className="font-medium">{t("Tu IM está publicado.")}</div>
              <div className="text-muted-foreground">
                {t("Solo pueden verlo los searchers e inversores cuyo NDA hayas aprobado desde tu panel, en la sección \"NDAs recibidos\".")}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
